import { Typography, CircularProgress, Box, Chip, Link } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle,
  Lock,
  TrackChangesOutlined,
  ApartmentOutlined,
  HeadsetMicOutlined,
  DescriptionOutlined,
  BarChartRounded,
} from '@mui/icons-material';
import {
  AGENTE_CONTABILIDADE_PRODUCT_ID,
  canAccessAgenteContabilidade,
} from '../../utils/agenteContabilidadeAccess';
import useContratosAtivos from '../../hooks/useContratosAtivos';

const produtosCompletos = [
  {
    id: 2,
    nome: 'Agente de Pr\u00e9 Venda',
    descricao: 'Solução inteligente para qualificação de leads',
    icon: TrackChangesOutlined,
    route: '/sdr',
  },
  {
    id: 1,
    nome: 'Agente do Corretor',
    descricao: 'Ferramenta de apoio especializada para corretores de imóveis',
    icon: ApartmentOutlined,
    route: '/assistente-vendas',
  },
  {
    id: 5,
    nome: 'Agente de Pós-Venda',
    descricao: 'Atendimento ao cliente especializado com foco no relacionamento pós-venda',
    icon: HeadsetMicOutlined,
    route: '/assistente-pos-venda',
  },
  {
    id: 3,
    nome: 'Agente de Conteúdo',
    descricao: 'Central de suporte e acompanhamento personalizado',
    icon: DescriptionOutlined,
    route: '/assistente-conteudo',
  },
];

const agenteContabilidadeProduto = {
  id: AGENTE_CONTABILIDADE_PRODUCT_ID,
  nome: 'Agente de Contabilidade',
  descricao: 'Gestao Contabil Completa',
  icon: BarChartRounded,
  route: '/agentecontabilidade',
  forceActive: true,
  hideConsumption: true,
};

export default function ProdutosUnificados() {
  const { contratosAtivos, loading } = useContratosAtivos();
  const navigate = useNavigate();

  const isCvcrmBrand =
    typeof window !== 'undefined' &&
    localStorage.getItem('brand') === 'cvcrm';

  const isProdutoContratado = (produtoId) => {
    return contratosAtivos.some((contrato) => contrato.idProduto === produtoId);
  };

  const getContratoInfo = (produtoId) => {
    return contratosAtivos.find((contrato) => contrato.idProduto === produtoId);
  };

  const produtosVisiveis = canAccessAgenteContabilidade()
    ? [...produtosCompletos, agenteContabilidadeProduto]
    : produtosCompletos;

  return (
    <>
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 'bold',
            color: 'text.primary',
            mb: 1,
            background: isCvcrmBrand
              ? 'linear-gradient(135deg, #41B28E 0%, #24695B 100%)'
              : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Nossos Agentes
        </Typography>
        <Typography
          variant="body1"
          sx={{ color: 'text.secondary', fontSize: '1.1rem' }}
        >
          Aqui você explora e personaliza os produtos contratados e aproveita ao máximo o
          potencial da tecnologia aplicada ao seu negócio.
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            lg: 'repeat(4, minmax(0, 1fr))',
          },
          alignItems: 'stretch',
          gap: { xs: 2, md: 3 },
          width: '100%',
        }}
        aria-label="Lista completa de produtos"
      >
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              py: 8,
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress
                size={50}
                sx={{ color: '#667eea' }}
                aria-label="Carregando informações dos produtos"
              />
              <Typography sx={{ mt: 2, color: 'text.secondary' }}>
                Carregando seus produtos...
              </Typography>
            </Box>
          </Box>
        ) : (
          produtosVisiveis.map((produto) => {
            const isContratado = produto.forceActive || isProdutoContratado(produto.id);
            const contratoInfo = produto.forceActive ? null : getContratoInfo(produto.id);

            return (
              <Box
                key={produto.id}
                sx={{
                  width: '100%',
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <ProductCardUnificado
                  produto={produto}
                  isContratado={isContratado}
                  contratoInfo={contratoInfo}
                  onClick={
                    isContratado && produto.route ? () => navigate(produto.route) : undefined
                  }
                />
              </Box>
            );
          })
        )}
      </Box>

      <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Chip
          icon={<CheckCircle />}
          label="Contratado"
          onClick={() => {}}
          clickable={false}
          sx={{
            bgcolor: '#e8f5e8',
            color: '#2e7d32',
            fontWeight: 'medium',
            '& .MuiChip-icon': { color: '#2e7d32' },
            cursor: 'default',
          }}
        />

        <Chip
          icon={<Lock />}
          label={'Dispon\u00edvel para Contrata\u00e7\u00e3o'}
          onClick={() => {}}
          clickable={false}
          sx={{
            bgcolor: '#f5f5f5',
            color: '#757575',
            fontWeight: 'medium',
            '& .MuiChip-icon': { color: '#757575' },
            cursor: 'default',
          }}
        />
      </Box>
    </>
  );
}

function ProductCardUnificado({ produto, isContratado, contratoInfo, onClick }) {
  const IconComponent = produto.icon;

  return (
    <Box
      onClick={onClick}
      sx={{
        bgcolor: 'background.paper',
        borderRadius: 3,
        p: { xs: 2.25, md: 2.5 },
        height: '100%',
        minHeight: '260px',
        cursor: isContratado && onClick ? 'pointer' : 'default',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        border: isContratado
          ? '2px solid #4caf50'
          : (muiTheme) => `2px solid ${muiTheme.palette.divider}`,
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        '&:hover': isContratado && onClick
          ? {
              transform: 'translateY(-8px)',
              boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
              borderColor: '#2e7d32',
            }
          : {
              transform: 'translateY(-2px)',
              boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
            },
        '&::before': isContratado
          ? {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(90deg, #4caf50, #81c784)',
            }
          : {},
      }}
    >
      <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
        {isContratado ? (
          <Chip
            icon={<CheckCircle sx={{ fontSize: '16px !important' }} />}
            label="Ativo"
            size="small"
            onClick={() => {}}
            clickable={false}
            sx={{
              bgcolor: '#e8f5e8',
              color: '#2e7d32',
              fontWeight: 'bold',
              fontSize: '0.75rem',
              '& .MuiChip-icon': { color: '#2e7d32' },
            }}
          />
        ) : (
          <Chip
            icon={<Lock sx={{ fontSize: '16px !important' }} />}
            label="Adquira"
            size="small"
            onClick={() => {}}
            clickable={false}
            sx={{
              bgcolor: '#f5f5f5',
              color: '#757575',
              fontWeight: 'bold',
              fontSize: '0.75rem',
              '& .MuiChip-icon': { color: '#757575' },
            }}
          />
        )}
      </Box>

      <Box
        sx={{
          width: 76,
          height: 76,
          mx: 'auto',
          mb: 1.5,
          borderRadius: 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          bgcolor: (muiTheme) =>
            muiTheme.palette.mode === 'dark'
              ? 'rgba(14, 21, 38, 0.94)'
              : 'rgba(248, 250, 255, 0.98)',
          border: '1px solid',
          borderColor: (muiTheme) =>
            isContratado
              ? 'rgba(102, 126, 234, 0.30)'
              : muiTheme.palette.divider,
          boxShadow: (muiTheme) =>
            muiTheme.palette.mode === 'dark'
              ? 'inset 0 1px 0 rgba(255,255,255,0.03), 0 8px 24px rgba(0,0,0,0.2)'
              : 'inset 0 1px 0 rgba(255,255,255,0.7), 0 10px 24px rgba(16,24,40,0.08)',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            borderRadius: 2.5,
            padding: '1px',
            background: isContratado
              ? 'linear-gradient(135deg, rgba(102,126,234,0.65), rgba(118,75,162,0.45))'
              : 'linear-gradient(135deg, rgba(148,163,184,0.28), rgba(148,163,184,0.1))',
            WebkitMask:
              'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
            WebkitMaskComposite: 'xor',
            maskComposite: 'exclude',
            pointerEvents: 'none',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 10,
            left: 10,
            width: 18,
            height: 3,
            borderRadius: 999,
            background: isContratado
              ? 'linear-gradient(90deg, #667eea, #764ba2)'
              : 'rgba(148,163,184,0.45)',
            pointerEvents: 'none',
          },
        }}
      >
        <IconComponent
          sx={{
            fontSize: 32,
            color: isContratado ? 'primary.main' : 'text.secondary',
            opacity: isContratado ? 0.96 : 0.82,
          }}
        />
      </Box>

      <Typography
        variant="h6"
        sx={{
          fontWeight: 'bold',
          mb: 1.5,
          color: isContratado ? 'text.primary' : 'text.secondary',
          textAlign: 'center',
          fontSize: { xs: '1.1rem', sm: '1.25rem' },
        }}
      >
        {produto.nome}
      </Typography>

      <Typography
        variant="body2"
        sx={{
          color: isContratado ? 'text.secondary' : 'text.disabled',
          lineHeight: 1.6,
          textAlign: 'center',
          mb: 1.5,
          flex: 1,
        }}
      >
        {contratoInfo?.descricaoPlano || produto.descricao}
      </Typography>

      {isContratado && (
        <Box
          sx={{
            mt: 'auto',
            pt: 1.5,
            borderTop: '1px solid #e8f5e8',
          }}
        >
          {contratoInfo && !produto.hideConsumption && (() => {
            const volumeConsumido = Number(contratoInfo?.atendimentos ?? 0);
            const limiteContrato = Number(contratoInfo?.limite ?? 0);
            const percentual = limiteContrato > 0 ? (volumeConsumido / limiteContrato) * 100 : 0;
            const percentualLimitado = Math.min(percentual, 100);
            const isExcedente = limiteContrato > 0 && volumeConsumido > limiteContrato;
            const corBarra = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';

            return (
              <Box sx={{ mb: 1.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: '#607d8b',
                    fontWeight: 'medium',
                    textAlign: 'center',
                    display: 'block',
                    mb: 0.75,
                  }}
                >
                  {limiteContrato > 0 ? (
                    <>
                      Consumo:{' '}
                      <Box
                        component="span"
                        sx={{ color: isExcedente ? '#d32f2f' : 'inherit' }}
                      >
                        {volumeConsumido}
                      </Box>{' '}
                      / {limiteContrato}
                    </>
                  ) : (
                    `Consumo: ${volumeConsumido}`
                  )}
                </Typography>
                {limiteContrato > 0 && (
                  <Box
                    sx={{
                      height: 8,
                      borderRadius: 999,
                      bgcolor: '#eceff1',
                      overflow: 'hidden',
                    }}
                    aria-label={`Consumo ${Math.round(percentual)}%`}
                  >
                    <Box
                      sx={{
                        width: `${percentualLimitado}%`,
                        height: '100%',
                        background: corBarra,
                        transition: 'width 0.3s ease',
                      }}
                    />
                  </Box>
                )}
              </Box>
            );
          })()}
          <Typography
            variant="caption"
            sx={{
              color: '#2e7d32',
              fontWeight: 'medium',
              textAlign: 'center',
              display: 'block',
            }}
          >
            Clique para acessar
          </Typography>
        </Box>
      )}

      {!isContratado && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: (muiTheme) =>
              muiTheme.palette.mode === 'dark'
                ? 'rgba(11,16,32,0.78)'
                : 'rgba(255,255,255,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: 0,
            transition: 'opacity 0.3s ease',
            '&:hover': {
              opacity: 1,
            },
          }}
        >
          <Box sx={{ textAlign: 'center', p: 2 }}>
            <Link
              href="https://api.whatsapp.com/send/?phone=5511917442081&text&type=phone_number&app_absent=0"
              target="_blank"
              rel="noopener noreferrer"
              underline="none"
              color="text.secondary"
              sx={{
                fontWeight: 'bold',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'none',
                  color: 'text.primary',
                },
                '&:focus': {
                  textDecoration: 'none',
                },
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 'bold',
                  color: 'text.secondary',
                  '&:hover': { textDecoration: 'underline', color: 'text.primary' },
                }}
              >
                Entre em contato para contratar
              </Typography>
            </Link>
          </Box>
        </Box>
      )}
    </Box>
  );
}
